import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { AttentionService } from '../api/AttentionAPI';
import { PaymentsService } from '../api/PaymentsAPI';
import { AppointmentsService } from '../api/AppointmentsAPI';
import { StaffService } from '../api/StaffAPI';
import { useLightMode, useTaxMode, shownQuote, staffLocation } from '../utils';
import { LOCATION_META, tint } from '../constants';
import { Header } from '../components/Header/Header';
import { DateRangeFilter } from '../components/DateRangeFilter/DateRangeFilter';
import { AppointmentSelect } from '../components/AppointmentSelect/AppointmentSelect';
import { AppointmentDrawer } from '../components/AppointmentDrawer/AppointmentDrawer';
import { SetPaymentMethodModal } from '../components/SetPaymentMethodModal/SetPaymentMethodModal';
import s from '../styles/staff.module.css';
import d from '../styles/dashboard.module.css';

const money = (cents) => cents == null
  ? '—'
  : `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// "Aug 24, 02:00 PM" — the viewer's own timezone, like the payments table.
const fmtWhen = (v) => v
  ? new Date(v).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

// The button each kind of loose end ends in.
const ACTION_LABEL = {
  take_payment: 'Take payment',
  assign_crew:  'Assign crew',
  find_car:     'Find the car',
  decide:       'Decide',
  verify:       'Verify',
  chase:        'Chase',
};

// One colour per bucket, as the mockup assigns them. The kind of loose end
// decides the colour, not simply whether it is money we are owed:
//
//   due    money that should be here and is not
//   warn   a decision for a person to make
//   gray   the money arrived; only the paperwork is unconfirmed
//   ar     owed by somebody else, on their own cycle
//
// A manual entry is the one case where the row disagrees with its group: the
// group is grey because nothing is missing, but the amount is real money that
// has already arrived, so it reads in the ordinary ink.
const GROUP_TONE = {
  finished_no_payment:    'attnToneDue',
  payment_no_appointment: 'attnToneDue',
  uncertain_match:        'attnToneWarn',
  tip_no_crew:            'attnToneWarn',
  awaiting_verification:  'attnToneGray',
  overdue:                'attnToneAr',
};
const AMOUNT_TONE = { ...GROUP_TONE, awaiting_verification: 'attnToneInk' };

// One chip per bucket, in the order the groups themselves come back, plus All.
// Short labels: the group heading below already carries the full wording.
// `tip_no_crew` has no counterpart in the mockup — it is ours, so it gets a
// chip like the rest rather than being the one bucket you cannot filter to.
const BUCKET_CHIPS = [
  { id: 'all',                    label: 'All' },
  { id: 'finished_no_payment',    label: 'No payment' },
  { id: 'payment_no_appointment', label: 'Unlinked money' },
  { id: 'uncertain_match',        label: 'Uncertain match' },
  { id: 'tip_no_crew',            label: 'Tip, no crew' },
  { id: 'awaiting_verification',  label: 'Manual, unverified' },
  { id: 'overdue',                label: 'Overdue elsewhere' },
];

const groupTone = (s, id) => s[GROUP_TONE[id]] || '';
const amountTone = (s, id) => s[AMOUNT_TONE[id]] || '';

const fullName = (m) => m.name || `${m.given_name || ''} ${m.family_name || ''}`.trim() || m.externalId;

const initials = (name) => String(name || '').split(' ').filter(Boolean)
  .map(w => w[0]).join('').slice(0, 4).toUpperCase();

// Grouped the way the queue sidebar groups them, so the same roster reads the
// same way in both places. There are no appointments loaded here, so each
// member falls back to the location on their own record.
function groupByLocation(staff) {
  const groups = new Map();
  for (const member of staff) {
    const locId = staffLocation(member, null);
    if (!groups.has(locId)) groups.set(locId, []);
    groups.get(locId).push(member);
  }
  const ordered = Object.keys(LOCATION_META).filter(id => groups.has(id));
  // Anyone we cannot place goes last rather than being dropped from the list.
  if (groups.has(null)) ordered.push(null);
  return ordered.map(id => [id, groups.get(id)]);
}

// Who did the car. The only thing that can settle a tip nobody can be paid, so
// the roster is on this page rather than a link away to the queue.
function CrewPicker({ item, staff, light, onCommit }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);

  // Picking is local and instant: the menu is a selection, not a series of
  // writes. Everything chosen is sent when the menu closes, so naming three
  // technicians is one decision rather than three round trips — and the row
  // cannot leave its bucket mid-selection and take the open menu with it.
  const startedWith = useRef(new Set(item.appt?.assignedStaff || []));
  const [assigned, setAssigned] = useState(() => new Set(startedWith.current));
  const [saving, setSaving] = useState(false);

  // Read by the close handlers, which bind once per open and would otherwise
  // see the selection as it was at the moment the menu opened.
  const assignedRef = useRef(assigned);
  assignedRef.current = assigned;

  const commit = async () => {
    const before = startedWith.current;
    const now = assignedRef.current;
    const added = [...now].filter(id => !before.has(id));
    const removed = [...before].filter(id => !now.has(id));
    if (!added.length && !removed.length) return;

    setSaving(true);
    const ok = await onCommit(item, added, removed, now.size);
    setSaving(false);
    if (ok) {
      startedWith.current = new Set(now);
    } else {
      // Put the ticks back to what the server still believes, rather than
      // leaving the menu claiming an assignment that never happened.
      setAssigned(new Set(before));
    }
  };

  // Every way out of the menu writes, not just the button.
  const closeRef = useRef(null);
  closeRef.current = () => { setOpen(false); commit(); };

  const toggle = () => {
    if (open) { closeRef.current(); return; }
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const GAP = 12;
      const width = 240;
      const below = window.innerHeight - r.bottom - GAP;
      const above = r.top - GAP;
      // Drop upward when a row near the bottom leaves no room below, rather
      // than running the roster off the screen where it cannot be reached.
      const flip = below < 240 && above > below;
      setPos({
        left: Math.max(GAP, Math.min(r.left, window.innerWidth - GAP - width)),
        width,
        maxHeight: Math.max(160, flip ? above : below),
        ...(flip ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => closeRef.current();
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (e.target?.closest?.('[data-crew-menu]')) return;
      close();
    };
    // Capture phase, so scrolling the page behind the menu closes it — the menu
    // is fixed and would otherwise hang in the wrong place. Scrolling *inside*
    // it is how a long roster is read, so that is left alone.
    const onScroll = (e) => {
      if (e.target?.closest?.('[data-crew-menu]')) return;
      close();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const pick = (member) => {
    setAssigned((prev) => {
      const out = new Set(prev);
      if (out.has(member.externalId)) out.delete(member.externalId);
      else out.add(member.externalId);
      return out;
    });
  };

  const label = saving
    ? 'Assigning…'
    : assigned.size
      ? `${assigned.size} assigned`
      : 'Assign crew';

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={s.attnBtn}
        onClick={toggle}
        disabled={saving}
      >
        {label}
      </button>
      {open && pos && createPortal(
        /* The portal lands outside the page's `.lightPage`, so carry the class
           across or the menu keeps its dark palette in light mode. */
        <div className={light ? s.lightPage : undefined}>
        <div
          data-crew-menu
          className={s.crewMenu}
          style={{ position: 'fixed', ...pos }}
        >
          {staff.length === 0 && <div className={s.crewEmpty}>No active staff on the roster</div>}
          {groupByLocation(staff).map(([locId, members]) => {
            const meta = LOCATION_META[locId];
            return (
              <div key={locId || 'unplaced'}>
                <div className={s.crewGroup}>
                  <span className={s.crewDot} style={{ background: meta?.color || 'var(--text-muted)' }} />
                  {meta?.name || 'Unassigned location'}
                </div>
                {members.map(m => {
                  const name = fullName(m);
                  const on = assigned.has(m.externalId);
                  return (
                    <button
                      key={m.externalId}
                      type="button"
                      className={`${s.crewItem} ${on ? s.crewItemOn : ''}`}
                      onClick={() => pick(m)}
                      aria-pressed={on}
                    >
                      <span
                        className={s.crewAvatar}
                        style={meta ? {
                          background: tint(meta.color, 13),
                          borderColor: tint(meta.color, 40),
                          color: meta.color,
                        } : undefined}
                      >
                        {initials(name)}
                      </span>
                      <span className={s.crewName}>{name}</span>
                      <span className={s.crewMark}>{on ? '✓' : ''}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
          <div className={s.crewFoot}>
            {assigned.size
              ? `${assigned.size} on this car · saved when you close this`
              : 'Tap each technician who worked on this car'}
          </div>
        </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function Row({ item, group, light, amount, staff, onOpen, onAction, onLink, onCommitCrew, busy }) {
  // An uncertain match has no booking yet, but it does have a proposed one —
  // open that, so a person can look at the car before accepting the match.
  const target = item.appt || item.suggestedAppt;
  // A payment with nothing to attach it to has nothing to open, so the row is
  // inert and the picker in the action slot does the work.
  const openable = !!target;
  // Both unlinked and uncertain payments are resolved by choosing a booking.
  const picks = item.action === 'find_car' || item.action === 'decide';
  return (
    <div
      className={`${s.attnRow} ${openable ? s.attnRowOpenable : ''}`}
      onClick={openable ? () => onOpen(target) : undefined}
      role={openable ? 'button' : undefined}
      tabIndex={openable ? 0 : undefined}
      onKeyDown={openable ? (e) => { if (e.key === 'Enter') onOpen(target); } : undefined}
    >
      <span className={s.attnWhen}>{fmtWhen(item.at)}</span>
      <span className={s.attnTitle}>{item.title}</span>
      <span className={s.attnDetail}>{item.detail}</span>
      <span className={`${s.attnAmount} ${amountTone(s, group.id)}`}>
        {money(amount)}
      </span>
      <span className={s.attnAction} onClick={(e) => e.stopPropagation()}>
        {item.action === 'assign_crew' ? (
          <CrewPicker item={item} staff={staff} light={light} onCommit={onCommitCrew} />
        ) : picks ? (
          <AppointmentSelect
            row={{
              orderId: item.orderId,
              bookId: item.bookId,
              customerId: item.customerId,
              appointment: item.suggestedAppt
                ? { startAt: item.suggestedAppt.startAt, totalAmount: item.suggestion?.totalAmount ?? null }
                : null,
            }}
            light={light}
            onChange={onLink}
          />
        ) : (
          <button
            type="button"
            className={s.attnBtn}
            disabled={busy}
            onClick={() => onAction(item)}
          >
            {ACTION_LABEL[item.action] || 'Open'}
          </button>
        )}
      </span>
    </div>
  );
}

export function NeedsAttentionPage() {
  const [lightMode, toggleLightMode] = useLightMode();
  const [taxMode, setTaxMode] = useTaxMode();

  // A row showing a price nobody has paid follows the switch; a row showing
  // money that arrived does not, because tax is already inside it.
  const shownAmount = (item) => (item.basis === 'quote'
    ? shownQuote(item.amountCents, item.taxCents, taxMode)
    : item.amountCents);
  // A range the Tips page can hand over, so "Resolve in Needs attention" lands
  // on the same dates the figure came from rather than on today.
  const [search] = useSearchParams();
  const linked = { start: search.get('start') || '', end: search.get('end') || '' };
  const [range, setRange] = useState(linked);
  const onRangeChange = useCallback((r) => setRange(r), []);
  const [detailAppt, setDetailAppt] = useState(null);
  const [payAppt, setPayAppt] = useState(null);
  const [payReturnTo, setPayReturnTo] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [bucket, setBucket] = useState('all');
  // Rows settled on this screen, hidden until the server stops returning them.
  const [resolved, setResolved] = useState(() => new Set());
  const [error, setError] = useState('');

  const queryClient = useQueryClient();
  const { data: staffData } = useQuery({
    queryKey: ['staff-roster'],
    queryFn: () => StaffService.list('active'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const staff = staffData?.staffs || [];

  const { data, isLoading, isError } = useQuery({
    queryKey: ['attention', range.start, range.end],
    queryFn: () => AttentionService.list(range.start, range.end),
    enabled: !!range.start && !!range.end,
  });

  // Everything still outstanding, before the bucket chips narrow it. The chip
  // counts come off this, so picking one never changes the numbers on the
  // others — a chip showing 3 would be a lie if choosing it made it say 0.
  const allGroups = useMemo(
    () => (data?.groups || [])
      .map(g => ({ ...g, items: g.items.filter(i => !resolved.has(i.key)) }))
      .filter(g => g.items.length),
    [data, resolved],
  );
  const groups = useMemo(
    () => (bucket === 'all' ? allGroups : allGroups.filter(g => g.id === bucket)),
    [allGroups, bucket],
  );
  const countFor = (id) => (id === 'all'
    ? allGroups.reduce((n, g) => n + g.items.length, 0)
    : allGroups.find(g => g.id === id)?.items.length || 0);
  const total = groups.reduce((n, g) => n + g.items.length, 0);

  // A key stops being hidden once the server agrees it is gone, so a row that
  // genuinely comes back — crew removed again later — is not hidden forever.
  useEffect(() => {
    if (!data) return;
    const stillListed = new Set((data.groups || []).flatMap(g => g.items.map(i => i.key)));
    setResolved((prev) => {
      const next = new Set([...prev].filter(k => stillListed.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [data]);
  useEffect(() => {
    if (bucket !== 'all' && !allGroups.some(g => g.id === bucket)) setBucket('all');
  }, [allGroups, bucket]);

  const payments = data?.payments || { total: 0, linkedItself: 0 };

  // The prefix, not just this date: the header badge counts today, which is a
  // different cache entry whenever an earlier day is being looked at.
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['attention'] });

  const openDetail = (appt) => { if (appt) setDetailAppt(appt); };

  // Every action either opens a panel or performs one call and refreshes.
  const runAction = async (item) => {
    setError('');
    if (item.action === 'take_payment' || item.action === 'chase') {
      // Both are settled in a panel rather than by a single call: the method
      // picker for a car with nothing recorded, the drawer for a chase.
      if (item.action === 'take_payment' && item.appt) {
        setPayReturnTo(null);
        setPayAppt(item.appt);
        return;
      }
      if (item.appt) setDetailAppt(item.appt);
      return;
    }
    if (item.action === 'verify') {
      setBusyKey(item.key);
      try {
        await PaymentsService.verify(item.idPayment);
        refresh();
      } catch (err) {
        setError(err?.message || 'Could not verify that payment');
      }
      setBusyKey(null);
    }
  };

  // Naming who did the car is what makes the tip payable, so the row leaves the
  // list as soon as the assignment lands.
  // Sent in one go when the picker closes. Sequential on purpose: two writes
  // racing on the same appointment both read its crew list before either has
  // written, and the first change is lost.
  const commitCrew = async (item, added, removed, crewCount) => {
    setError('');
    try {
      for (const staffId of added) {
        await AppointmentsService.updateStaffAssignment({
          externalId: item.bookId, staffId, assign: true,
        });
      }
      for (const staffId of removed) {
        await AppointmentsService.updateStaffAssignment({
          externalId: item.bookId, staffId, assign: false,
        });
      }
      // Drop the row now rather than after the refetch. The car has crew, so
      // it is no longer a tip nobody can be paid — leaving it sitting there
      // reading "2 assigned" until the round trip lands invites someone to
      // assign the same car twice.
      if (crewCount > 0) {
        setResolved(prev => new Set(prev).add(item.key));
      }
      refresh();
      // The tips report credits this the moment the crew exists.
      queryClient.invalidateQueries({ queryKey: ['tip-ledger'] });
      return true;
    } catch (err) {
      setError(err?.message || 'Could not save that crew');
      return false;
    }
  };

  const linkAppointment = async (orderId, bookId) => {
    setError('');
    try {
      await PaymentsService.setAppointment(orderId, bookId);
      refresh();
    } catch (err) {
      setError(err?.message || 'Could not link that appointment');
    }
  };

  const refreshRow = () => {
    refresh();
    queryClient.invalidateQueries({ queryKey: ['appointment-payment'] });
  };

  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <Header lightMode={lightMode} toggleLightMode={toggleLightMode}
        taxMode={taxMode} onTaxModeChange={setTaxMode} />

      <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
        <div>
          <div className={s.tableHeaderRow}>
            <div className={s.sectionLabel} style={{ marginBottom: 0 }}>
              Everything the software could not settle on its own
            </div>
          </div>

          <div className={s.attnHead}>
            <h1 className={s.attnHeading}>Needs attention</h1>
            <div className={s.attnSummary}>
              <div>{payments.linkedItself} of {payments.total} payments linked themselves.</div>
              <div>These are the ones that need a person.</div>
            </div>
          </div>

          <div className={s.filterRow}>
            <div className={s.ledgerIntro} style={{ marginLeft: 0 }}>
              {total} item{total === 1 ? '' : 's'} in {data?.range?.label || 'this range'}
            </div>
            <DateRangeFilter
              onChange={onRangeChange}
              includeToday
              initialStart={linked.start}
              initialEnd={linked.end}
            />
          </div>

          {/* Which kind of loose end to look at. An empty bucket keeps its chip
              rather than disappearing, so the row does not reshuffle under the
              pointer as items get resolved — it just cannot be selected. */}
          <div className={s.chipRow} style={{ margin: '10px 0 4px' }}>
            {BUCKET_CHIPS.map((c) => {
              const n = countFor(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  disabled={!n && c.id !== 'all'}
                  onClick={() => setBucket(c.id)}
                  className={`${s.chip} ${bucket === c.id ? s.chipActive : ''}`}
                >
                  {c.label}{n > 0 ? ` ${n}` : ''}
                </button>
              );
            })}
          </div>

          {error && <div className={s.attnError}>{error}</div>}
          {isLoading && <div className={s.ledgerIntro}>Loading…</div>}
          {isError && <div className={s.attnError}>Couldn’t load the attention list.</div>}

          {!isLoading && !isError && !groups.length && (
            <div className={s.attnEmpty}>
              {bucket === 'all'
                ? 'Nothing needs a person in this range. Every payment settled itself.'
                : 'Nothing in this bucket for this range.'}
            </div>
          )}

          {groups.map((g) => (
            <section key={g.id} className={s.attnGroup}>
              <div className={s.attnGroupHead}>
                <span className={`${s.attnGroupLabel} ${groupTone(s, g.id)}`}>
                  {g.label}
                </span>
                {g.note && <span className={s.attnGroupNote}>{g.note}</span>}
                <span className={`${s.attnGroupTotal} ${groupTone(s, g.id)}`}>
                  {g.id === 'uncertain_match'
                    ? `${g.count} payment${g.count === 1 ? '' : 's'}`
                    : money(g.items.reduce((n, i) => n + (shownAmount(i) || 0), 0))}
                </span>
              </div>

              {g.items.map((item) => (
                <Row
                  key={item.key}
                  item={item}
                  group={g}
                  amount={shownAmount(item)}
                  staff={staff}
                  onCommitCrew={commitCrew}
                  light={lightMode}
                  busy={busyKey === item.key}
                  onOpen={openDetail}
                  onAction={runAction}
                  onLink={linkAppointment}
                />
              ))}
            </section>
          ))}
        </div>
      </div>

      {/* The drawer and the method panel read their light theme from a
          `.lightRoot` ancestor in dashboard.module.css; this page's wrapper uses
          staff.module.css. `display: contents` gives the selector something to
          match without adding a box. */}
      <div className={lightMode ? d.lightRoot : undefined} style={{ display: 'contents' }}>
        {detailAppt && (
          <AppointmentDrawer
            appt={detailAppt}
            staff={[]}
            onClose={() => setDetailAppt(null)}
            onVerified={refreshRow}
            onTakePayment={(a) => { setDetailAppt(null); setPayReturnTo(a); setPayAppt(a); }}
            onChangeMethod={(a) => { setDetailAppt(null); setPayReturnTo(a); setPayAppt(a); }}
          />
        )}

        {payAppt && (
          <SetPaymentMethodModal
            appt={payAppt}
            onClose={() => {
              setPayAppt(null);
              if (payReturnTo) setDetailAppt(payReturnTo);
              setPayReturnTo(null);
            }}
            onSaved={refreshRow}
          />
        )}
      </div>
    </div>
  );
}
